import { motion } from 'framer-motion';
import { ArrowDown, Play } from 'lucide-react';
import { useState } from 'react';
import { COMPANY, STATS, FACTORY_VIDEO, LOGO_URL } from '../data/mock';

export const HeroSection = () => {
  const [videoPlaying, setVideoPlaying] = useState(false);

  const scrollToAbout = () => {
    const el = document.querySelector('#about');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Video Background */}
      <div className="absolute inset-0 z-0">
        <video
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover"
          poster="https://images.unsplash.com/photo-1657688332253-9b4eb735d6d8?w=1920&q=80"
        >
          <source src={FACTORY_VIDEO} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0D1B2A]/80 via-[#0D1B2A]/60 to-[#0D1B2A]/90" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0D1B2A]/70 to-transparent" />
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-20 right-10 w-96 h-96 bg-[#D4952B]/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-10 w-64 h-64 bg-[#147DAB]/10 rounded-full blur-3xl" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-20 w-full">
        <div className="max-w-3xl">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-md mb-8"
          >
            <span className="w-2 h-2 rounded-full bg-[#D4952B] animate-pulse" />
            <span className="text-white/70 text-sm font-medium tracking-wide">{COMPANY.heroTagline}</span>
          </motion.div>

          {/* Main Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="text-5xl md:text-7xl font-bold text-white leading-[1.08] mb-6"
          >
            {COMPANY.slogan}
            <span className="block text-[#E8B84A] mt-2 text-3xl md:text-4xl font-semibold" dir="rtl">
              {COMPANY.sloganAr}
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="text-lg md:text-xl text-white/60 leading-relaxed mb-10 max-w-xl font-light"
          >
            {COMPANY.subSlogan}
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8 }}
            className="flex flex-wrap gap-4"
          >
            <button
              onClick={() => document.querySelector('#products')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-4 bg-[#D4952B] hover:bg-[#C08525] text-white font-semibold rounded-xl transition-all duration-300 shadow-xl shadow-amber-900/30 hover:shadow-amber-800/50 hover:-translate-y-0.5 text-sm tracking-wide"
            >
              Explore Our Products
            </button>
            <button
              onClick={() => setVideoPlaying(true)}
              className="group px-8 py-4 bg-white/5 hover:bg-white/10 border border-white/15 hover:border-white/30 text-white font-semibold rounded-xl transition-all duration-300 backdrop-blur-md flex items-center gap-3 text-sm tracking-wide"
            >
              <span className="w-8 h-8 rounded-full bg-white/10 group-hover:bg-white/20 flex items-center justify-center transition-colors">
                <Play className="w-3.5 h-3.5 fill-white text-white" />
              </span>
              Watch Our Factory
            </button>
          </motion.div>
        </div>

        {/* Stats Bar */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 rounded-2xl overflow-hidden backdrop-blur-xl border border-white/10"
        >
          {STATS.map((stat, i) => (
            <div key={i} className="bg-white/[0.03] px-6 py-6 md:py-8 text-center hover:bg-white/[0.06] transition-colors duration-500">
              <div className="text-3xl md:text-4xl font-bold text-[#E8B84A] mb-1">{stat.value}</div>
              <div className="text-white/50 text-xs md:text-sm font-medium tracking-wide">{stat.label}</div>
              <div className="text-white/30 text-[10px] mt-0.5 font-medium" dir="rtl">{stat.labelAr}</div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.button
        onClick={scrollToAbout}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/40 hover:text-white/70 transition-colors cursor-pointer"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase font-medium">Scroll</span>
        <ArrowDown className="w-4 h-4 animate-bounce" />
      </motion.button>

      {/* Video Modal */}
      {videoPlaying && (
        <div
          className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setVideoPlaying(false)}
        >
          <div className="relative w-full max-w-4xl aspect-video rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <video
              autoPlay
              controls
              className="w-full h-full object-contain bg-black"
            >
              <source src={FACTORY_VIDEO} type="video/mp4" />
            </video>
            <button
              onClick={() => setVideoPlaying(false)}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
            >
              <span className="text-xl">&times;</span>
            </button>
          </div>
        </div>
      )}
    </section>
  );
};
